import { EMRdetailsProps, IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import Input from "@/app/components/ui/Inputs/Input";
import MandatoryLabel from "@/app/components/ui/Reusable/MandatoryLabel";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useUtils } from "@/app/hooks/useUtils";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

interface PetDetailsProps {
  pet_tempeture: string | null;
  pet_dob: string | null;
  pet_species_type_id: string | null;
  pet_weight: string | null;
  day: string | null;
  month: string | null;
  year: string | null;
}

type SectionType = "pet_tempeture" | "pet_dob" | "pet_species_type_id" | "pet_weight" | "day" | "month" | "year";

interface PetProfileComponentProps extends EMRdetailsProps {
  hideTemperature?: boolean;
}

const PetProfileComponent = ({
  emrDetails,
  setEmrDetails,
  errorMessage,
  setErrorMessage,
  hideTemperature = false,
}: PetProfileComponentProps) => {
  const isVideoConsultation = emrDetails?.emr_type === "Video Consultation";
  const { pet_species } = useArrayConstants();
  const { convertToValidDateStringFormat, getCurrentDate } = useUtils();
  const { forOnlyNumbersWithDecimal, forNumericWithDecimalAndNotStartFromZero } = useFormValidations();

  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const formHook = useForm<PetDetailsProps>({
    mode: "onChange",
  });

  const debouncedUpdate = useMemo(
    () =>
      debounce((updatedBody: Partial<IEMRUpdatePayloadProps>) => {
        updateEmrAPI(emr_id, {
          ...updatedBody,
          emr_status: emrDetails?.emr_status ?? emr_status,
        }).then(({ status, message }) => {
          if (status) setEmrDetails(message);
        });
      }, 1500),
    [emr_id, updateEmrAPI, emrDetails?.emr_status],
  );

  const resolvePetSpeciesId = useCallback(
    (specie?: string | null) => {
      if (!specie) return undefined;
      const normalized = specie.trim().toLowerCase();
      const matched = pet_species?.find((item) => {
        const title = item.title?.trim().toLowerCase();
        if (title === normalized) return true;
        if (normalized === "canine" && title === "dog") return true;
        if (normalized === "feline" && title === "cat") return true;
        return false;
      });
      return matched?.id;
    },
    [pet_species],
  );

  const resolvePetSpecieName = useCallback(
    (speciesId?: string | null) => {
      if (!speciesId) return undefined;
      const matched = pet_species?.find((item) => item.id === speciesId);
      const normalizedTitle = matched?.title?.trim().toLowerCase();
      if (normalizedTitle === "dog") return "Canine";
      if (normalizedTitle === "cat") return "Feline";
      return matched?.title;
    },
    [pet_species],
  );

  useEffect(() => {
    if (emrDetails?.pet_details) {
      const petDetails = emrDetails.pet_details;
      const currentSpeciesId = formHook.getValues("pet_species_type_id");
      const resolvedSpeciesId =
        resolvePetSpeciesId(petDetails.pet_specie) ?? petDetails.pet_species_type_id ?? currentSpeciesId;

      formHook.reset({
        pet_dob: petDetails.pet_age,
        pet_species_type_id: resolvedSpeciesId,
        pet_tempeture: petDetails.pet_temperature,
        pet_weight: petDetails.pet_weight_kg,
      });

    }
  }, [
    emrDetails?.pet_details?.pet_age,
    emrDetails?.pet_details?.pet_temperature,
    emrDetails?.pet_details?.pet_weight_kg,
    emrDetails?.pet_details?.pet_species_type_id,
    emrDetails?.pet_details?.pet_specie,
    resolvePetSpeciesId,
  ]);



  const allValues = formHook.watch();

  const handleInputChange = useCallback(
    (section: SectionType, value: string | number) => {
      const nextSpeciesId =
        section === "pet_species_type_id" ? String(value) : allValues.pet_species_type_id;
      const nextPetSpecie =
        resolvePetSpecieName(nextSpeciesId) ?? emrDetails?.pet_details?.pet_specie;

      // Prevent triggering API for non-positive weights
      if (section === "pet_weight") {
        const numericWeight = Number(value);
        if (Number.isNaN(numericWeight) || numericWeight <= 0) {
          return;
        }
      }

      const updatedPetDetails: Partial<IEMRUpdatePayloadProps> = {
        pet_details: {
          pet_specie: nextPetSpecie,
          pet_species_type_id: nextSpeciesId,
          pet_age: section === "pet_dob" ? value : allValues.pet_dob,
          pet_temperature: section === "pet_tempeture" ? value : allValues.pet_tempeture,
          pet_weight_kg: section === "pet_weight" ? value : allValues?.pet_weight, // use new value from event, not from watch()
          pet_temperature_unit: "F",
        },
      };

      debouncedUpdate(updatedPetDetails);
      setErrorMessage((prev: string[] | null) => {
        if (!prev) return prev;

        let updated = [...prev];

        if (section === "pet_tempeture") {
          updated = updated.filter((msg) => msg !== "Temperature");
        }

        if (section === "pet_weight") {
          updated = updated.filter((msg) => msg !== "Weight");
        }

        return updated;
      });
    },
    [debouncedUpdate, allValues, emrDetails?.pet_details?.pet_specie, resolvePetSpecieName],
  );

  return (
    <div className=" w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">
        Pet Details <MandatoryLabel label="*" className="text-xs text-[#ED0101] " />{" "}
      </h3>
      <div className="bg-[#F9FAFB] w-full  rounded-xl p-2 flex flex-wrap sm:flex-nowrap gap-4 sm:gap-6 items-center mt-2">
        <div className="w-full sm:w-[182px]">
          <p className="text-xs mb-1">Species</p>
          <SearchableSelectMenu
            errorMessages={[{ message: "", type: "required" }]}
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue("pet_species_type_id", item.id);
                handleInputChange("pet_species_type_id", item.id);
              }
            }}
            fieldError={formHook?.formState?.errors?.pet_species_type_id}
            register={formHook.register(`pet_species_type_id`, { required: true })}
            selectItems={pet_species}
            placeholder="Select Specie"
            showTooltips={true}
            showTypedErrors={true}
            showDropdownIcon={true}
            defaultSelected={pet_species?.filter((oc) => oc.id === formHook.watch(`pet_species_type_id`))[0]}
            listBoxClassName="w-full"
            className="text-[#000000] h-9"
            containerClassName="w-full"
          />
        </div>
        <div className="flex  flex-col w-full sm:w-[182px]">
          <p className="text-xs mb-1">DOB</p>
          <PWLCalendarComponent
            showLeftNav={false}
            allowPastDaySelection={true}
            errorMessages={[{ type: "required", message: "Please select valid DOB" }]}
            register={formHook.register("pet_dob", {
              required: true,
            })}
            showTypedErrors
            disableDatesAfterDate={getCurrentDate()}
            defaultSelectedDate={formHook.watch("pet_dob")}
            disabledFutureDateYearMonthSelection
            fieldError={formHook.formState.errors.pet_dob}
            onSelectDate={(date) => {
              const dobString = convertToValidDateStringFormat(date);
              formHook.setValue("pet_dob", dobString);
              handleInputChange("pet_dob", dobString);
            }}
            className="w-full sm:w-[182px] h-9"
          />


        </div>
        {!isVideoConsultation && !hideTemperature && (
          <div className="w-full sm:w-[192px]">
            <p className="text-xs mb-1">
              Temperature{" "}
              {errorMessage?.includes("Temperature") && <span className="text-red-500 pl-2 text-xs">Required</span>}
            </p>

            <Input
              endIcon={<span className="text-gray-500 text-xs">&deg;F</span>}
              className="text-[#000000] rounded-md w-full sm:w-[192px] h-9 text-sm"
              inputClassName="text-xs h-9"
              placeholder="Temperature"
              register={formHook.register("pet_tempeture", {
                required: false,
                ...forOnlyNumbersWithDecimal.validations,
                max: { value: 106, message: "Temperature cannot exceed 106" },
                onChange: (e) => handleInputChange("pet_tempeture", e.target.value),
              })}
              fieldError={formHook.formState.errors.pet_tempeture}
              errorMessages={[
                forOnlyNumbersWithDecimal.errors,
                { type: "max", message: "Temperature cannot exceed 106" },
              ]}
            />
          </div>
        )}
        <div className="w-full sm:w-[192px]">
          <p className="text-xs mb-1">
            Weight {errorMessage?.includes("Weight") && <span className="text-red-500 pl-2 text-xs">Required</span>}
          </p>

          <Input
            maxLength={10}
            endIcon={<span className="text-gray-500  text-xs">Kg</span>}
            className="text-[#000000] rounded-md w-full sm:w-[192px] h-9 text-sm"
            inputClassName="text-xs h-9"
            placeholder="Weight"
            register={formHook.register("pet_weight", {
              maxLength: 10,
              required: false,
              ...forNumericWithDecimalAndNotStartFromZero.validations,
              onChange: (e) => {
                const value = e.target.value;
                const numericWeight = Number(value);
                if (value === "" || Number.isNaN(numericWeight) || numericWeight <= 0) {
                  formHook.setError("pet_weight", {
                    type: "manual",
                    message: value ? "Weight must be greater than 0" : "",
                  });
                  return;
                }
                formHook.clearErrors("pet_weight");
                handleInputChange("pet_weight", value);
              },
            })}
            fieldError={formHook.formState.errors.pet_weight}
            errorMessages={[
              forNumericWithDecimalAndNotStartFromZero.errors,
              { type: "maxLength", message: "Invalid weight" },
              {
                type: "manual",
                message:
                  Number(formHook.watch("pet_weight")) <= 0 && formHook.watch("pet_weight")
                    ? "Weight must be greater than 0"
                    : "",
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};
export default PetProfileComponent;
